import { useCallback, useRef } from 'react';

// Every bar docked to the bottom of the screen registers its live height here:
// message composers, and the shifts calendar's outreach selection bar. The
// floating chat launcher (see app_layout.tsx) lifts above
// `--composer-footer-height`, so the variable must reflect the tallest docked
// bar currently on screen and only clear once none remain — otherwise
// unmounting one of several composers would drop the launcher back onto a
// still-visible send button.
//
// The CSS variable name predates the calendar's use of this hook and is a
// published contract with app_layout.tsx; it is left alone deliberately.
const composerFooterHeights = new Map<HTMLElement, number>();

// Launchers that cannot read a CSS variable — Intercom positions its bubble
// through a JavaScript config value — subscribe for the same number instead.
const dockedFooterHeightListeners = new Set<(heightPx: number) => void>();

function getTallestDockedFooterHeightPx(): number {
    return composerFooterHeights.size === 0
        ? 0
        : Math.max(...composerFooterHeights.values());
}

/**
 * Observe the tallest currently docked bottom bar, in pixels. Calls back
 * immediately with the current value, then on every change, and returns an
 * unsubscribe function.
 */
export function subscribeToDockedFooterHeight(
    listener: (heightPx: number) => void,
): () => void {
    dockedFooterHeightListeners.add(listener);
    listener(getTallestDockedFooterHeightPx());
    return () => {
        dockedFooterHeightListeners.delete(listener);
    };
}

function publishComposerFooterHeight(): void {
    const tallest = getTallestDockedFooterHeightPx();
    for (const listener of dockedFooterHeightListeners) {
        listener(tallest);
    }
    if (typeof document === 'undefined') {
        return;
    }
    const root = document.documentElement;
    if (composerFooterHeights.size === 0) {
        root.style.removeProperty('--composer-footer-height');
        return;
    }
    root.style.setProperty('--composer-footer-height', `${tallest}px`);
}

export function useComposerFooterHeight() {
    // Publish the composer footer's live height as a CSS variable so overlays
    // pinned to the viewport corner (e.g. the floating chat launcher) can sit
    // above it. Measured at runtime because the footer's height varies with the
    // device safe-area inset, the typing indicator, the character counter, and
    // multi-line drafts — a fixed offset cannot clear all of those.
    const composerFooterObserverRef = useRef<ResizeObserver | null>(null);
    const observedComposerFooterRef = useRef<HTMLDivElement | null>(null);
    const setComposerFooterNode = useCallback((node: HTMLDivElement | null) => {
        composerFooterObserverRef.current?.disconnect();
        composerFooterObserverRef.current = null;
        if (observedComposerFooterRef.current) {
            composerFooterHeights.delete(observedComposerFooterRef.current);
            observedComposerFooterRef.current = null;
            publishComposerFooterHeight();
        }

        if (
            !node ||
            typeof document === 'undefined' ||
            typeof ResizeObserver === 'undefined'
        ) {
            return;
        }

        observedComposerFooterRef.current = node;
        const publishHeight = () => {
            composerFooterHeights.set(node, node.offsetHeight);
            publishComposerFooterHeight();
        };

        publishHeight();
        const observer = new ResizeObserver(publishHeight);
        observer.observe(node);
        composerFooterObserverRef.current = observer;
    }, []);

    return setComposerFooterNode;
}
